// Browser WebSocket client for bidirectional sync
// Handles connection lifecycle, reconnection, message queuing, and event broadcasting
//
// TODO (audit Priority 2 — sync architecture audit, 2026-05-12):
// - Convert this header into a full @fileoverview JSDoc block per the
//   project code-documentation rule (Responsibilities, Dependencies,
//   Architecture, Notes). Add @contract blocks for the public message
//   shapes (re-exported from ./types) and @process for connect(),
//   handleClose() reconnection backoff, send() queueing-while-offline,
//   and the heartbeat loop. The transport works correctly; what is
//   missing is the structured wire-protocol documentation that would
//   let a new contributor understand it without reading the whole file.
// - Add WebSocket reconnection stress tests covering: reconnection with
//   pending pushes, reconnection while a divergence check is running,
//   and rapid online/offline cycling (currently only covered indirectly).

import {
  WebSocketClientConfig,
  WebSocketMessage,
  ClientMessage,
  SyncEvent,
  ConnectionStatus,
  ConnectionState,
  SyncEventListener,
  ConnectionStateListener,
  PullRequest,
  PullResponse,
  PushRequest,
  PushResponse,
} from './types';
import { debugLog } from '../utils/debugLog';
import { recordRequestSample } from '../services/connectionQuality';

/**
 * Server error codes that indicate a session authentication failure (not a
 * permission or operational error). Sourced from mobile-api / tenant-api
 * authMiddleware (`TOKEN_VALIDATION_FAILED`) and actorResolver /
 * validateMobileSyncAccountIds (`AUTH_REQUIRED`, `AUTH_IDENTITY_INVALID`,
 * `AUTH_LOOKUP_FAILED`). `UNAUTHORIZED` is included for forward-compat with
 * any route that returns a generic 401-style code. When any of these codes
 * arrives via a WebSocket `error` message, the client fires `onAuthFailure`
 * so the application can clear the session and surface re-login — it does
 * NOT reconnect automatically (the token is invalid).
 */
const AUTH_FAILURE_ERROR_CODES = new Set([
  'TOKEN_VALIDATION_FAILED',
  'AUTH_REQUIRED',
  'AUTH_IDENTITY_INVALID',
  'AUTH_LOOKUP_FAILED',
  'UNAUTHORIZED',
]);

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private config: WebSocketClientConfig & {
    reconnectIntervalMs: number;
    maxReconnectDelayMs: number;
    reconnectJitterRatio: number;
    heartbeatIntervalMs: number;
    messageTimeoutMs: number;
    maxConcurrentPulls: number;
  };
  private status: ConnectionStatus = {
    state: 'disconnected',
    reconnectAttempts: 0,
  };
  
  private reconnectTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private isIntentionallyClosed = false;
  // Timestamp of the last heartbeat ping send; pong latency is sampled into
  // the connection-quality monitor as a free RTT probe.
  private lastPingSentAt: number | null = null;
  
  private eventListeners = new Set<SyncEventListener>();
  private statusListeners = new Set<ConnectionStateListener>();
  private subscriptions = new Set<string>();
  
  private pendingRequests = new Map<string, {
    resolve: (value: any) => void;
    reject: (error: Error) => void;
    timeout: number;
  }>();
  
  private messageQueue: ClientMessage[] = [];
  private requestIdCounter = 0;

  // Page lifecycle: on iOS, the WKWebView process is frozen when the PWA
  // goes to the background and the WebSocket is killed silently (no clean
  // `onclose`). When `enableVisibilityReconnect` is set we listen for
  // `visibilitychange` so we can force a reconnect the moment the app
  // returns to the foreground. Left off for desktop apps.
  private visibilityHandler: (() => void) | null = null;

  // Per-socket pull concurrency gate. `activePulls` counts slots taken;
  // `pullSlotWaiters` holds pulls waiting on a free slot. Each waiter can be
  // resolved (slot granted) or rejected (socket torn down before a slot freed).
  private activePulls = 0;
  private pullSlotWaiters: Array<{ resolve: () => void; reject: (e: Error) => void }> = [];

  constructor(config: WebSocketClientConfig) {
    this.config = {
      reconnectIntervalMs: 1000,
      maxReconnectDelayMs: 30000,
      reconnectJitterRatio: 0.5,
      heartbeatIntervalMs: 15000,
      messageTimeoutMs: 30000,
      maxConcurrentPulls: 4,
      ...config,
    };
  }

  // Connection management
  async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      debugLog('[WebSocketClient] Already connected or connecting');
      return;
    }

    this.isIntentionallyClosed = false;
    this.updateStatus('connecting');
    if (this.config.enableVisibilityReconnect) {
      this.startVisibilityListener();
    }
    
    try {
      const url = await this.buildWebSocketUrl();
      debugLog('[WebSocketClient] Connecting to:', url);
      this.ws = new WebSocket(url);
      this.setupWebSocketHandlers();
    } catch (error: any) {
      console.error('[WebSocketClient] Failed to create WebSocket:', error);
      this.updateStatus('failed', error.message);
      this.scheduleReconnect();
    }
  }

  disconnect(): void {
    debugLog('[WebSocketClient] Disconnecting...');
    this.isIntentionallyClosed = true;
    if (this.config.enableVisibilityReconnect) {
      this.stopVisibilityListener();
    }
    this.cleanup();
    this.updateStatus('disconnected');
  }

  // Event subscription
  onSyncEvent(listener: SyncEventListener): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  onStatusChange(listener: ConnectionStateListener): () => void {
    this.statusListeners.add(listener);
    // Immediately notify of current status
    listener(this.status);
    return () => this.statusListeners.delete(listener);
  }

  getStatus(): ConnectionStatus {
    return { ...this.status };
  }

  // Collection subscriptions
  subscribe(collections: string | string[]): void {
    const cols = Array.isArray(collections) ? collections : [collections];
    
    for (const col of cols) {
      this.subscriptions.add(col);
    }

    if (this.isConnected()) {
      this.send({
        type: 'subscribe',
        collections: cols,
      });
    }
  }

  unsubscribe(collections: string | string[]): void {
    const cols = Array.isArray(collections) ? collections : [collections];
    
    for (const col of cols) {
      this.subscriptions.delete(col);
    }

    if (this.isConnected()) {
      this.send({
        type: 'unsubscribe',
        collections: cols,
      });
    }
  }

  // Sync operations
  async pull(request: PullRequest): Promise<PullResponse> {
    // Bound concurrent in-flight pulls so a reconnect that wakes every
    // collection loop at once cannot flood the single socket. Excess pulls
    // wait here for a free slot and dispatch as earlier ones settle.
    await this.acquirePullSlot();
    try {
      const requestId = this.generateRequestId();
      return await this.sendWithResponse<PullResponse>(
        {
          type: 'pull',
          requestId,
          pullData: request,
        },
        requestId
      );
    } finally {
      this.releasePullSlot();
    }
  }

  private acquirePullSlot(): Promise<void> {
    if (this.activePulls < this.config.maxConcurrentPulls) {
      this.activePulls++;
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => this.pullSlotWaiters.push({ resolve, reject }));
  }

  private releasePullSlot(): void {
    const next = this.pullSlotWaiters.shift();
    if (next) {
      // Hand the slot straight to the next waiter — activePulls stays the same.
      next.resolve();
    } else {
      this.activePulls = Math.max(0, this.activePulls - 1);
    }
  }

  // Reject every pull still waiting for a slot. Called on socket teardown so a
  // queued pull does not hang forever once its predecessors are failed.
  private drainPullWaiters(reason: string): void {
    // Reset the concurrency counter: every teardown path calls
    // failPendingRequests() immediately before this, abandoning all in-flight
    // pulls, so the next connection must start from a clean gate. Without this,
    // the async releasePullSlot() calls in each failed pull's finally block can
    // lag behind a fast reconnect and leave activePulls over-counted, throttling
    // the first pulls on the fresh socket. This matters more now that visibility
    // reconnect drives frequent close/reconnect cycles on mobile. Any late
    // releasePullSlot() is clamped by the Math.max(0, …) guard.
    this.activePulls = 0;
    if (this.pullSlotWaiters.length === 0) return;
    const waiters = this.pullSlotWaiters;
    this.pullSlotWaiters = [];
    for (const w of waiters) {
      w.reject(new Error(reason));
    }
  }

  async push(request: PushRequest): Promise<PushResponse> {
    const requestId = this.generateRequestId();
    
    return this.sendWithResponse<PushResponse>(
      {
        type: 'push',
        requestId,
        pushData: request,
      },
      requestId
    );
  }

  // Private methods
  private async buildWebSocketUrl(): Promise<string> {
    const url = new URL(this.config.url);
    // Convert http/https to ws/wss, but preserve ws/wss if already set
    if (url.protocol === 'https:') {
      url.protocol = 'wss:';
    } else if (url.protocol === 'http:') {
      url.protocol = 'ws:';
    }
    // else: already ws: or wss:, keep as-is
    
    // Preserve base path and append /ws
    // e.g., http://localhost:3002 → ws://localhost:3002/ws (local tenant-api, container path is /api/ws)
    // e.g., https://domain.com → wss://domain.com/ws (prod tenant-api via Front Door /api/ws route)
    // e.g., https://domain.com/m/api → wss://domain.com/m/api/ws (prod mobile-api)
    const basePath = url.pathname.replace(/\/$/, ''); // Remove trailing slash
    url.pathname = `${basePath}/ws`;
    
    // Get fresh token on each connection
    const token = typeof this.config.token === 'function' 
      ? await this.config.token()
      : this.config.token;
    
    url.searchParams.set('token', token);
    if (this.config.tenantSlug) {
      url.searchParams.set('_tenant', this.config.tenantSlug);
    }
    if (this.config.syncRoutePrefix) {
      url.searchParams.set('_syncRoute', this.config.syncRoutePrefix);
    }
    return url.toString();
  }

  private setupWebSocketHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      debugLog('[WebSocketClient] WebSocket opened, waiting for server connected message...');
      this.status.reconnectAttempts = 0;
      this.startHeartbeat();
    };

    this.ws.onclose = (event) => {
      console.log('[WebSocketClient] Disconnected:', event.code, event.reason);
      const closeReason = String(event.reason || '');
      // Auth-specific close reasons sent by the server with code 1008:
      //   'Missing authentication token', 'Invalid or expired token'.
      // Other 1008 closes ('Missing departmentId', 'Tenant not found',
      //   'Missing tenant slug') are configuration errors, not session auth
      // failures — they must not trigger session invalidation. Require both
      // the word 'token' and an auth-related adjective so a generic 'Invalid
      // message format' close does not log the user out.
      const authFailure = event.code === 1008
        && /token/i.test(closeReason)
        && /invalid|expired|missing|authentication/i.test(closeReason);
      if (authFailure) {
        this.config.onAuthFailure?.(closeReason || `WebSocket closed with code ${event.code}`);
      }
      this.stopHeartbeat();

      // Fast-fail any in-flight request/response calls. Without this, requests
      // issued on the now-dead socket sit until their individual messageTimeout
      // (e.g. 30s) fires, so a brief drop stalls the sync engine for the full
      // timeout instead of letting it retry on the fresh connection. Reuse the
      // 'WebSocket disconnected' message the pull/push helpers treat as a benign
      // cancellation (debug log + retry), not an error.
      this.failPendingRequests('WebSocket disconnected');
      this.drainPullWaiters('WebSocket disconnected');

      if (authFailure) {
        // Auth rejection — notify the app so it can clear the session and
        // surface re-login, but STILL reconnect. The token config is a function
        // that fetches a FRESH token on each connect(), so when MSAL eventually
        // acquires a valid token (after interactive login or silent refresh),
        // the next reconnect attempt will succeed. Stopping reconnection here
        // would permanently kill sync with no recovery path — the WebSocket
        // client has no "restart after auth recovery" hook, and nothing in the
        // replication factory calls connect() again after onAuthFailure fires.
        // The exponential backoff (capped at maxReconnectDelayMs) bounds the
        // retry rate so the loop is not a tight spin.
        this.updateStatus('reconnecting', 'Authentication rejected — retrying');
        this.scheduleReconnect();
      } else if (!this.isIntentionallyClosed) {
        this.updateStatus('reconnecting');
        this.scheduleReconnect();
      } else {
        this.updateStatus('disconnected');
      }
    };

    this.ws.onerror = () => {
      // Do NOT log the raw error Event: its `target`/`currentTarget` is the
      // WebSocket whose `url` carries the auth token as a query param, so
      // logging the event leaks the token into the console. The browser emits
      // its own (non-suppressible) error line; the following `onclose` carries
      // the useful close code. Log only a safe, token-free message here.
      const errorMsg = 'WebSocket connection error';
      console.error('[WebSocketClient] WebSocket error (see close code below)');
      this.updateStatus('failed', errorMsg);
    };

    this.ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        this.handleMessage(message);
      } catch (error) {
        console.error('[WebSocketClient] Failed to parse message:', error);
      }
    };
  }

  private handleMessage(message: WebSocketMessage): void {
    switch (message.type) {
      case 'connected':
        debugLog('[WebSocketClient] ⚠️ RECEIVED CONNECTED MESSAGE - Server time:', message.serverTime);
        debugLog('[WebSocketClient] Now subscribing to collections...');
        this.updateStatus('connected', undefined, new Date());
        this.resubscribeCollections();
        this.flushMessageQueue();
        break;

      case 'pong':
        // Heartbeat response received — record ping→pong RTT as a link sample.
        if (this.lastPingSentAt !== null) {
          recordRequestSample(Date.now() - this.lastPingSentAt);
          this.lastPingSentAt = null;
        }
        break;

      case 'upsert':
      case 'delete':
        if (message.collection) {
          this.broadcastSyncEvent({
            type: message.type,
            collection: message.collection,
            documentId: message.documentId,
            updatedAt: message.updatedAt,
          });
        }
        break;

      case 'push-ack':
        if (message.requestId && message.pushAck) {
          this.resolveRequest(message.requestId, message.pushAck);
        }
        break;

      case 'pull-response':
        if (message.requestId && message.pullResponse) {
          this.resolveRequest(message.requestId, message.pullResponse);
        }
        break;

      case 'error': {
        const errorCode = String((message.error as any)?.code || '');
        const errorMessage = String(message.error?.message || 'Unknown error');
        // Match auth-specific error codes sent by the server (see
        // mobile-api/tenant-api authMiddleware and actorResolver):
        //   TOKEN_VALIDATION_FAILED, AUTH_REQUIRED, AUTH_IDENTITY_INVALID,
        //   AUTH_LOOKUP_FAILED, UNAUTHORIZED.
        // Non-auth error codes (INVALID_COLLECTION, PUSH_FAILED,
        // COLLECTION_NOT_WRITABLE, CASE_PERMISSION_DENIED) must NOT trigger
        // session invalidation. The tight regex fallback catches servers that
        // send a free-text message without a structured code — it requires both
        // 'token' and an auth adjective to avoid false positives like "Expired
        // cache entry" or "Invalid message format".
        if (AUTH_FAILURE_ERROR_CODES.has(errorCode)
          || (/\btoken\b/i.test(errorMessage)
            && /invalid|expired|missing|authentication/i.test(errorMessage))) {
          this.config.onAuthFailure?.(errorMessage);
        }
        if (message.requestId) {
          this.rejectRequest(message.requestId, new Error(errorMessage));
        } else {
          console.error('[WebSocketClient] Server error:', message.error);
        }
        break;
      }

      default:
        console.warn('[WebSocketClient] Unknown message type:', message);
    }
  }

  private send(message: ClientMessage): void {
    if (!this.isConnected()) {
      debugLog('[WebSocketClient] Not connected, queueing message:', message.type);
      this.messageQueue.push(message);
      return;
    }

    try {
      debugLog('[WebSocketClient] Sending message:', message.type, message.type === 'subscribe' ? { collections: (message as any).collections } : '');
      this.ws!.send(JSON.stringify(message));
    } catch (error) {
      console.error('[WebSocketClient] Send failed:', error);
      this.messageQueue.push(message);
    }
  }

  private async sendWithResponse<T>(message: ClientMessage, requestId: string): Promise<T> {
    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(new Error(`Request ${requestId} timed out`));
      }, this.config.messageTimeoutMs);

      this.pendingRequests.set(requestId, { resolve, reject, timeout });
      this.send(message);
    });
  }

  private resolveRequest(requestId: string, value: any): void {
    const pending = this.pendingRequests.get(requestId);
    if (pending) {
      clearTimeout(pending.timeout);
      pending.resolve(value);
      this.pendingRequests.delete(requestId);
    }
  }

  private rejectRequest(requestId: string, error: Error): void {
    const pending = this.pendingRequests.get(requestId);
    if (pending) {
      clearTimeout(pending.timeout);
      pending.reject(error);
      this.pendingRequests.delete(requestId);
    }
  }

  // Reject every in-flight request/response call at once. Used on both clean
  // teardown and unexpected socket close so callers fail fast instead of
  // waiting out their per-message timeout on a connection that is already gone.
  private failPendingRequests(reason: string): void {
    if (this.pendingRequests.size === 0) return;
    const pending = Array.from(this.pendingRequests.values());
    this.pendingRequests.clear();
    for (const req of pending) {
      clearTimeout(req.timeout);
      req.reject(new Error(reason));
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = window.setInterval(() => {
      if (this.isConnected()) {
        this.lastPingSentAt = Date.now();
        this.send({ type: 'ping' });
      }
    }, this.config.heartbeatIntervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Force a clean reconnect on a connection that looks alive but has stopped
   * making progress (the stuck-sync watchdog calls this). Closing the live
   * socket runs the normal onclose path — fail-pending, drain pull waiters,
   * scheduleReconnect — so the engine resumes on a fresh connection (and, on
   * a freshly-scaled-out replica, re-runs the readiness handshake). No-op when
   * the socket was closed intentionally (logout / account switch).
   */
  forceReconnect(reason = 'forced reconnect'): void {
    if (this.isIntentionallyClosed) {
      return;
    }
    console.warn('[WebSocketClient] Forcing reconnect:', reason);
    // A forced reconnect must happen promptly — e.g. the app just returned to
    // the foreground and the socket has been dead for a while. Drop any pending
    // backoff timer and reset the attempt counter first: otherwise the socket
    // was likely already CLOSED with a long backoff timer pending (up to
    // maxReconnectDelayMs), and scheduleReconnect() would no-op on that existing
    // timer — so the reconnect would wait out the old backoff instead of firing
    // now. Clearing + resetting guarantees the next schedule uses the base delay.
    this.clearReconnectTimer();
    this.status.reconnectAttempts = 0;
    const ws = this.ws;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      // onclose fires synchronously-ish and handles failPending + reconnect.
      try { ws.close(); } catch { /* ignore */ }
    } else {
      // No live socket to close — fail anything pending and schedule directly.
      this.failPendingRequests(reason);
      this.drainPullWaiters(reason);
      this.scheduleReconnect();
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null || this.isIntentionallyClosed) {
      return;
    }

    const capped = Math.min(
      this.config.reconnectIntervalMs * Math.pow(2, this.status.reconnectAttempts),
      this.config.maxReconnectDelayMs
    );

    // Apply equal jitter so a fleet that drops together does not reconnect in
    // lockstep and stampede a single replica. Delay stays in
    // [capped * (1 - ratio), capped]. See reconnectJitterRatio @contract.
    const ratio = Math.min(Math.max(this.config.reconnectJitterRatio, 0), 1);
    const delay = Math.round(capped * (1 - ratio) + Math.random() * capped * ratio);

    debugLog(`[WebSocketClient] Reconnecting in ${delay}ms (attempt ${this.status.reconnectAttempts + 1}, base ${capped}ms, jitter ${ratio})`);

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.status.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  private resubscribeCollections(): void {
    if (this.subscriptions.size > 0) {
      debugLog('[WebSocketClient] Resubscribing to collections:', Array.from(this.subscriptions));
      this.send({
        type: 'subscribe',
        collections: Array.from(this.subscriptions),
      });
    }
  }

  private flushMessageQueue(): void {
    if (this.messageQueue.length === 0) return;

    debugLog(`[WebSocketClient] Flushing ${this.messageQueue.length} queued messages`);
    const queue = [...this.messageQueue];
    this.messageQueue = [];

    for (const message of queue) {
      this.send(message);
    }
  }

  // ---------------------------------------------------------------------------
  // Page-lifecycle listeners (iOS background freeze / foreground resume)
  // ---------------------------------------------------------------------------

  private startVisibilityListener(): void {
    if (this.visibilityHandler) return; // already listening
    if (typeof document === 'undefined') return; // non-browser (SSR/tests)
    this.visibilityHandler = () => {
      if (document.visibilityState === 'hidden') {
        // iOS freezes the WKWebView shortly after; close cleanly so the
        // server drops the stale connection immediately. Also close an
        // in-progress (CONNECTING) handshake — iOS would freeze it and it would
        // fail silently, leaving a half-open socket.
        debugLog('[WebSocketClient] Page hidden — closing socket preemptively');
        if (
          this.ws &&
          (this.ws.readyState === WebSocket.OPEN ||
            this.ws.readyState === WebSocket.CONNECTING)
        ) {
          try { this.ws.close(); } catch { /* ignore */ }
        }
      } else if (document.visibilityState === 'visible') {
        // App returned to foreground — force reconnect if the socket is
        // gone (iOS killed it) or still thinks it is connected (stale).
        debugLog('[WebSocketClient] Page visible — forcing reconnect');
        this.forceReconnect('page became visible after background');
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private stopVisibilityListener(): void {
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    this.visibilityHandler = null;
  }

  private cleanup(): void {
    this.stopHeartbeat();

    this.clearReconnectTimer();

    // Reject all pending requests and any pulls queued behind the concurrency gate
    this.failPendingRequests('WebSocket disconnected');
    this.drainPullWaiters('WebSocket disconnected');

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      
      this.ws = null;
    }
  }

  private isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private updateStatus(state: ConnectionState, lastError?: string, connectedAt?: Date): void {
    this.status = {
      state,
      connectedAt: connectedAt || this.status.connectedAt,
      lastError,
      reconnectAttempts: this.status.reconnectAttempts,
    };

    for (const listener of this.statusListeners) {
      listener(this.status);
    }
  }

  private broadcastSyncEvent(event: SyncEvent): void {
    for (const listener of this.eventListeners) {
      try {
        listener(event);
      } catch (error) {
        console.error('[WebSocketClient] Event listener error:', error);
      }
    }
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${++this.requestIdCounter}`;
  }
}
